import type { FC } from 'react';
import React, { useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { mergeRefs } from 'use-callback-ref';

import Tooltip from '@atlaskit/tooltip';
import VideoPauseOverlayIcon from '@atlaskit/icon/core/video-pause-overlay';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';
import Spinner from '@atlaskit/spinner';
import { IconButton } from '@atlaskit/button/new';

const i18n = defineMessages({
	pauseButtonLabel: {
		id: 'audio.play-pause-button.pause-label',
		defaultMessage: 'Pause',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will pause the actively playing audio',
	},
	playButtonLabel: {
		id: 'audio.play-pause-button.play-label',
		defaultMessage: 'Play',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will play the actively paused audio',
	},
	loadingButtonLabel: {
		id: 'audio.play-pause-button.loading-label',
		defaultMessage: 'Loading',
		description:
			'The label is used as a tooltip and for accessibility for the icon button when the audio is loading',
	},
});

export type PlayPauseButtonProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	isPlaying: boolean;
	isLoading: boolean;
	onClick?: () => void;
	hasFetched?: boolean;
	fetchContent?: () => void;
};

export const PlayPauseButton: FC<PlayPauseButtonProps> = ({
	audioRef,
	isPlaying,
	isLoading,
	onClick = () => {},
	hasFetched = true,
	fetchContent,
}) => {
	const { formatMessage } = useIntl();
	const pressableRef = useRef<HTMLButtonElement>(null);
	const updateTooltip = useRef<() => void>();

	useEffect(() => {
		if (pressableRef.current) {
			pressableRef.current.focus();
		}
	}, [isPlaying]);

	const { labelValue, buttonIcon, testId } = useMemo(() => {
		if (isLoading) {
			return {
				labelValue: formatMessage(i18n.loadingButtonLabel),
				buttonIcon: Spinner,
				testId: 'miniplayer-loading',
			};
		}
		if (isPlaying) {
			return {
				labelValue: formatMessage(i18n.pauseButtonLabel),
				buttonIcon: VideoPauseOverlayIcon,
				testId: 'miniplayer-pause',
			};
		}
		return {
			labelValue: formatMessage(i18n.playButtonLabel),
			buttonIcon: VideoPlayOverlayIcon,
			testId: 'miniplayer-play',
		};
	}, [isLoading, isPlaying, formatMessage]);

	useLayoutEffect(() => {
		updateTooltip.current?.();
	}, [isLoading, isPlaying]);

	const handlePause = () => {
		if (audioRef.current) {
			audioRef.current.pause();
		}
	};

	const handlePlay = async () => {
		if (audioRef.current && hasFetched) {
			await audioRef.current.play();
		} else {
			handlePause();
			fetchContent?.();
		}
	};

	const handleClick = async () => {
		if (isPlaying) {
			handlePause();
		} else {
			await handlePlay();
		}
		onClick?.();
	};

	return (
		<Tooltip
			content={({ update }) => {
				updateTooltip.current = update;
				return labelValue;
			}}
		>
			{({ onClick, ref: tooltipRef, ...tooltipProps }) => (
				<IconButton
					icon={buttonIcon}
					onClick={handleClick}
					isDisabled={isLoading}
					ref={mergeRefs([pressableRef, tooltipRef])}
					testId={testId}
					{...tooltipProps}
					spacing="default"
					appearance="primary"
					label={labelValue}
					shape="circle"
				/>
			)}
		</Tooltip>
	);
};
